import { Permit2 } from "@sablier/models";
import { EXPONENT_DECIMALS } from "@sablier/v2-constants";
import {
  DEFAULT_BROKER_ADDRESS,
  DEFAULT_BROKER_FEE_PERCENTAGE,
} from "@sablier/v2-constants";
import { framework } from "@sablier/v2-contracts";
import { _ } from "@sablier/v2-mixins";
import { contractor, peripheral } from "~/client/utils";
import type { IDynamicExponential } from "./config";
import type { IAddress, IWagmiAddress } from "@sablier/v2-types";
import type {
  IExtensionParamsGroup,
  IExtensionParamsSingle,
  IExtensionResultGroupDeltas,
  IExtensionResultGroupMilestones,
  IExtensionResultSingleDeltas,
  IExtensionResultSingleMilestones,
  IProxyExecuteResult,
} from "~/client/types";
import { precompute } from "./setup";

type IExtension = IDynamicExponential;

/**
 * ------------------------------
 * Explicit function overloads
 * ------------------------------
 */

async function processSingle(
  params: IExtensionParamsSingle & { timing: "duration" },
): Promise<{
  target: IExtensionResultSingleDeltas;
  proxy: IProxyExecuteResult;
}>;
async function processSingle(
  params: IExtensionParamsSingle & { timing: "range" },
): Promise<{
  target: IExtensionResultSingleMilestones;
  proxy: IProxyExecuteResult;
}>;
async function processSingle(params: IExtensionParamsSingle): Promise<{
  target: IExtensionResultSingleMilestones | IExtensionResultSingleDeltas;
  proxy: IProxyExecuteResult;
}>;

async function processSingle({
  dependencies,
  extras,
  timing,
  permit2,
}: IExtensionParamsSingle): Promise<{
  target: IExtensionResultSingleMilestones | IExtensionResultSingleDeltas;
  proxy: IProxyExecuteResult;
}> {
  /**
   * ------------------------------
   * Setup dependencies
   * ------------------------------
   */

  const { purpose, ...extended } = extras as IExtension;
  const {
    address,
    cancelability,
    chainId,
    duration,
    end,
    sender,
    start,
    token,
    proxy,
  } = dependencies;

  const contract: IAddress = contractor(
    chainId!,
    purpose,
    token?.address,
  ).address;
  const targeted = permit2 ? "targetPermit2" : "targetApprove";
  const target: IAddress = peripheral(chainId, targeted).address;
  const transferData = await Permit2.createTargetEncoding(permit2);

  const { amount: deposit } = precompute.single({ dependencies });
  const endTime = _.toValuePrepared({ humanized: end, decimals: -3 });
  const startTime = _.toValuePrepared({ humanized: start, decimals: -3 });

  const totalDuration = _.toValuePrepared({
    humanized: duration,
    decimals: -3,
  });

  const exponent = _.toValuePrepared({
    humanized: extended.exponent,
    decimals: EXPONENT_DECIMALS,
  });

  /**
   * ------------------------------
   * Setup dependencies: DYNAMIC
   * ------------------------------
   */

  if (timing === "duration") {
    type Inputs = IExtensionResultSingleDeltas["inputs"];

    const segments = [
      {
        amount: _.toBigInt(deposit),
        exponent: _.toBigInt(exponent),
        delta: _.toNumber(
          _.toValuePrepared({ raw: totalDuration, decimals: 0 }),
        ),
      },
    ] as const;

    const inputs: Inputs = [
      contract as IWagmiAddress,
      {
        sender: sender as IWagmiAddress,
        recipient: address as IWagmiAddress,
        totalAmount: _.toBigInt(deposit),
        segments,
        asset: token!.address as IWagmiAddress,
        cancelable: cancelability!,
        broker: {
          account: DEFAULT_BROKER_ADDRESS as IWagmiAddress,
          fee: _.toBigInt(DEFAULT_BROKER_FEE_PERCENTAGE),
        },
      },
      transferData as IWagmiAddress,
    ];

    const data = framework.contextualize(
      target,
      chainId!,
      targeted,
      "createWithDeltas",
      inputs,
    );

    const calldata = await framework.encode(
      targeted,
      "createWithDeltas",
      inputs,
    );
    return {
      target: data,
      proxy: framework.contextualize(proxy, chainId!, "proxy", "execute", [
        target as IWagmiAddress,
        calldata,
      ]),
    };
  } else {
    type Inputs = IExtensionResultSingleMilestones["inputs"];

    const segments = [
      {
        amount: _.toBigInt(deposit),
        exponent: _.toBigInt(exponent),
        milestone: _.toNumber(endTime),
      },
    ] as const;

    const inputs: Inputs = [
      contract as IWagmiAddress,
      {
        sender: sender as IWagmiAddress,
        recipient: address as IWagmiAddress,
        totalAmount: _.toBigInt(deposit),
        startTime: _.toNumber(startTime),
        segments,
        asset: token!.address as IWagmiAddress,
        cancelable: cancelability!,
        broker: {
          account: DEFAULT_BROKER_ADDRESS as IWagmiAddress,
          fee: _.toBigInt(DEFAULT_BROKER_FEE_PERCENTAGE),
        },
      },
      transferData as IWagmiAddress,
    ];

    const data = framework.contextualize(
      target,
      chainId!,
      targeted,
      "createWithMilestones",
      inputs,
    );

    const calldata = await framework.encode(
      targeted,
      "createWithMilestones",
      inputs,
    );
    return {
      target: data,
      proxy: framework.contextualize(proxy, chainId!, "proxy", "execute", [
        target as IWagmiAddress,
        calldata,
      ]),
    };
  }
}

/**
 * ------------------------------
 * Explicit function overloads
 * ------------------------------
 */

type IResultGroup = {
  target: IExtensionResultGroupDeltas | IExtensionResultGroupMilestones;
  proxy: IProxyExecuteResult;
};

async function processGroup(
  params: IExtensionParamsGroup & { timing: "duration" },
): Promise<{
  target: IExtensionResultGroupDeltas;
  proxy: IProxyExecuteResult;
}>;
async function processGroup(
  params: IExtensionParamsGroup & { timing: "range" },
): Promise<{
  target: IExtensionResultGroupMilestones;
  proxy: IProxyExecuteResult;
}>;
async function processGroup(
  params: IExtensionParamsGroup,
): Promise<IResultGroup>;

async function processGroup({
  dependencies,
  purpose,
  timing,
  permit2,
}: IExtensionParamsGroup): Promise<IResultGroup> {
  /**
   * ------------------------------
   * Setup dependencies
   * ------------------------------
   */

  const { cancelability, chainId, sender, token, streams, proxy, signer } =
    dependencies;

  const lockup = contractor(chainId, purpose, token?.address).address;
  const targeted = permit2 ? "targetPermit2" : "targetApprove";
  const target: IAddress = peripheral(chainId, targeted).address;
  const transferData = await Permit2.createTargetEncoding(permit2);

  /**
   * ------------------------------
   * Prepare transaction parameters
   * ------------------------------
   */

  if (timing === "duration") {
    type Inputs = IExtensionResultGroupDeltas["inputs"];

    const params: Inputs["2"] = await Promise.all(
      streams?.map(async (stream) => {
        const { address, amount, duration, end, start } = stream;

        const single = await processSingle({
          dependencies: {
            address,
            amount,
            cancelability,
            duration,
            end,
            token,
            start,
            chainId,
            sender,
            proxy: "",
            signer,
          },
          extras: stream.extension,
          timing: "duration",
          permit2,
        });

        return single.target.inputs[1];
      }) || [],
    );

    const inputs: Inputs = [
      lockup as IWagmiAddress,
      token!.address as IWagmiAddress,
      params,
      transferData as IWagmiAddress,
    ] as const;

    const data = framework.contextualize(
      target,
      chainId!,
      targeted,
      "batchCreateWithDeltas",
      inputs,
    );

    const calldata = await framework.encode(
      targeted,
      "batchCreateWithDeltas",
      inputs,
    );

    return {
      target: data,
      proxy: framework.contextualize(proxy, chainId!, "proxy", "execute", [
        target as IWagmiAddress,
        calldata,
      ]),
    };
  } else {
    type Inputs = IExtensionResultGroupMilestones["inputs"];

    const params: Inputs["2"] = await Promise.all(
      streams?.map(async (stream) => {
        const { address, amount, duration, end, start } = stream;

        const single = await processSingle({
          dependencies: {
            address,
            amount,
            cancelability,
            duration,
            end,
            token,
            start,
            chainId,
            sender,
            proxy: "",
            signer,
          },
          extras: stream.extension,
          timing: "range",
          permit2,
        });

        return single.target.inputs[1];
      }) || [],
    );

    const inputs: Inputs = [
      lockup as IWagmiAddress,
      token!.address as IWagmiAddress,
      params,
      transferData as IWagmiAddress,
    ];

    const data = framework.contextualize(
      target,
      chainId!,
      targeted,
      "batchCreateWithMilestones",
      inputs,
    );

    const calldata = await framework.encode(
      targeted,
      "batchCreateWithMilestones",
      inputs,
    );

    return {
      target: data,
      proxy: framework.contextualize(proxy, chainId!, "proxy", "execute", [
        target as IWagmiAddress,
        calldata,
      ]),
    };
  }
}

export const process = { group: processGroup, single: processSingle };
